<script setup lang="ts">

const { t } = useI18n()
const nuxtApp = useNuxtApp()

const shopCartStore = useShopCartStore()
const { shopCart, loadingShopCart } = storeToRefs(shopCartStore)
const router = useRouter()

const {
    loadingDelete,
    loadingUpdate,
    loadingCheck,
    quantityValidationMsg,
    quantityProduct,
    editCountProduct,
    rowProduct,
    editRow,
    validateQuantity,
    addQuantityProduct,
    changeQuantityProduct,
    destroyProduct: deleteProduct,
    lessQuantityProduct,
} = useCartItems()
const loadingFinishCart = ref(false)
onMounted(() => {
    nuxtApp.$bus.$on("update:shop-cart", () => {
        shopCartStore.fetchShopCart(undefined)
    })
})

const clearCart = () => {
    shopCartStore.deleteShopCart()
}

const finishCart = () => {
    loadingFinishCart.value=true
    router.push('/cart').finally(() => {
        loadingFinishCart.value=false
    })
}

</script>

<template>
    <div class="tw-flex tw-flex-col tw-h-[calc(100dvh-45px)] tw-justify-between tw-w-full">
        <div class="tw-flex tw-flex-col tw-w-full">
            <div class='tw-flex tw-flex-col tw-self-start tw-items-start tw-gap-2 tw-w-full tw-py-2 tw-divide-y tw-divide-home-deli-gray tw-divide-2 tw-px-2 tw-border'
                :class="{ '!tw-divide-none': shopCart.items.length == 0 }">
                <div class="tw-flex tw-flex-col tw-w-full " v-for="item in shopCart.items as CartItem[]" :key="item.id"
                    v-if="shopCart.items.length > 0 && !loadingShopCart">
                    <div class="tw-grid tw-grid-cols-6 tw-w-full tw-gap-2 tw-items-center">
                        <q-img :src="item.productible.images[0].origin" class="tw-w-full tw-h-auto tw-m-1"
                            v-if="item.productible?.images[0]?.origin" />
                        <q-img :src="'/img/product_placeholder.png'" class="tw-w-full tw-h-auto tw-m-1" v-else />
                        <div
                            class="tw-col-span-5 tw-flex tw-flex-row tw-justify-between tw-items-center tw-gap-2 tw-pr-2">
                            <div class="tw-flex tw-flex-col tw-gap-2 tw-no-wrap">
                                <p class="tw-text-titles tw-text-lg">{{ item.productible.name }}</p>
                                <div class="tw-flex tw-flex-row tw-gap-4">
                                    <p>{{ item.productible.price_formatted }}</p>
                                    <p>{{ t('cartItems.quantity', { quantity: item.quantity }) }}</p>
                                    <p>{{ t('cartItems.subtotal', { subtotal: item.subtotal_formatted }) }}</p>
                                </div>
                            </div>
                            <q-btn flat dense icon="fa-solid fa-trash" size="xs" :loading="loadingDelete" color="negative" class="tw-cursor-pointer" @click="deleteProduct(item)">
                                <q-tooltip>
                                    {{ t('actions.delete') }}
                                </q-tooltip>
                            </q-btn>
                        </div>

                    </div>
                </div>
                <div v-else-if="loadingShopCart">
                    <CartSkeletonListCart :items="shopCart.items.length > 0 ? shopCart.items.length - 1 : 0" />
                </div>
                <div v-else class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full">
                    <div class="tw-flex tw-flex-col tw-items-start tw-justify-center tw-h-full">
                        <div class="tw-w-[50%] tw-flex tw-justify-end">
                            <svg width="403" height="338" viewBox="0 0 403 338" fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M316.099 254.55H95.1375C85.4919 254.55 79.3816 243.909 84.0406 235.219L91.405 221.498L67.6708 75.9477L15 67.304"
                                    stroke="#808080" stroke-width="30" stroke-miterlimit="10" stroke-linecap="round" />
                                <path
                                    d="M308.251 193.282L145.319 206.931C144.453 207.002 143.593 206.739 142.905 206.194C142.217 205.649 141.751 204.86 141.598 203.981L123.639 100.915C123.549 100.397 123.57 99.8649 123.701 99.3565C123.832 98.848 124.071 98.3754 124.399 97.9719C124.727 97.5684 125.137 97.2438 125.601 97.021C126.064 96.7983 126.57 96.6827 127.081 96.6826H323.564C324.076 96.6821 324.581 96.7971 325.045 97.0197C325.509 97.2422 325.92 97.5668 326.248 97.9705C326.576 98.3742 326.814 98.8471 326.945 99.3558C327.076 99.8646 327.097 100.397 327.006 100.915L311.41 190.34C311.274 191.12 310.89 191.832 310.318 192.365C309.747 192.897 309.02 193.22 308.251 193.282Z"
                                    fill="#cdbf34" stroke="#F5990A" stroke-width="20" stroke-miterlimit="10" />
                                <path d="M197.74 198.965L194.674 96.6826" stroke="#F5990A" stroke-width="20"
                                    stroke-miterlimit="10" />
                                <path d="M259.479 195.942L262.468 96.6826" stroke="#F5990A" stroke-width="20"
                                    stroke-miterlimit="10" />
                                <path
                                    d="M147.968 311.673C147.968 314.876 147.045 318.007 145.314 320.669C143.584 323.332 141.124 325.408 138.247 326.634C135.369 327.859 132.203 328.18 129.148 327.555C126.093 326.93 123.287 325.388 121.084 323.123C118.882 320.859 117.382 317.973 116.774 314.832C116.167 311.691 116.478 308.435 117.67 305.476C118.862 302.517 120.881 299.988 123.471 298.209C126.061 296.43 129.105 295.48 132.22 295.48C136.397 295.48 140.402 297.186 143.356 300.223C146.309 303.26 147.968 307.378 147.968 311.673Z"
                                    fill="#606060" stroke="#606060" stroke-width="20" stroke-miterlimit="10" />
                                <path
                                    d="M290.113 313.348C291.007 304.453 284.718 296.496 276.067 295.577C267.415 294.657 259.677 301.124 258.783 310.019C257.889 318.915 264.177 326.872 272.829 327.791C281.481 328.711 289.219 322.244 290.113 313.348Z"
                                    fill="#606060" stroke="#606060" stroke-width="20" stroke-miterlimit="10" />
                                <path
                                    d="M170.48 23.8553C200.354 -6.86583 248.79 -6.86583 278.668 23.8553C308.545 54.5765 308.542 104.378 278.668 135.096C248.793 165.814 200.354 165.814 170.48 135.096C140.606 104.378 140.606 54.5733 170.48 23.8553Z"
                                    fill="white" />
                                <path
                                    d="M179.672 33.3114C204.48 7.80505 244.677 7.80505 269.473 33.3114C294.269 58.8178 294.281 100.144 269.473 125.641C244.665 151.138 204.471 151.148 179.672 125.641C154.873 100.135 154.876 58.8085 179.672 33.3114Z"
                                    fill="#FF5B00" />
                                <path
                                    d="M360.334 63.5535C359.777 63.5317 359.239 63.3409 358.787 63.005C358.336 62.6692 357.991 62.2033 357.797 61.6663C357.603 61.1293 357.568 60.5451 357.696 59.9875C357.824 59.43 358.11 58.9239 358.518 58.5333L386.714 33.4631C386.985 33.2043 387.303 33.003 387.65 32.8707C387.997 32.7383 388.366 32.6775 388.736 32.6917C389.105 32.7059 389.469 32.7949 389.805 32.9535C390.141 33.1121 390.444 33.3373 390.696 33.6162C390.947 33.8951 391.143 34.2222 391.272 34.5789C391.401 34.9355 391.46 35.3148 391.446 35.695C391.432 36.0751 391.346 36.4488 391.191 36.7946C391.037 37.1404 390.818 37.4516 390.547 37.7104L362.354 62.7805C362.084 63.0398 361.766 63.2416 361.419 63.3742C361.072 63.5069 360.704 63.5678 360.334 63.5535Z"
                                    fill="#F5990A" stroke="#F5990A" stroke-width="10" stroke-miterlimit="10" />
                                <path
                                    d="M395.005 81.6757L368.602 84.0671C368.234 84.1033 367.862 84.0645 367.508 83.9529C367.154 83.8414 366.825 83.6592 366.54 83.4169C366.254 83.1746 366.018 82.8768 365.845 82.5406C365.671 82.2045 365.564 81.8364 365.528 81.4576C365.493 81.0787 365.531 80.6964 365.639 80.3325C365.748 79.9686 365.925 79.6303 366.161 79.3368C366.396 79.0433 366.686 78.8004 367.013 78.622C367.34 78.4436 367.698 78.3332 368.066 78.297L394.487 75.9055C395.231 75.833 395.973 76.0674 396.549 76.5571C397.125 77.0468 397.489 77.7518 397.559 78.517C397.63 79.2822 397.402 80.0448 396.926 80.6372C396.449 81.2296 395.764 81.6031 395.019 81.6757H395.005Z"
                                    fill="#F5990A" stroke="#F5990A" stroke-width="10" stroke-miterlimit="10" />
                                <path
                                    d="M341.924 52.3698C341.205 52.1587 340.598 51.663 340.234 50.9915C339.871 50.32 339.781 49.5276 339.985 48.7883L346.832 22.7003C346.934 22.3343 347.104 21.9923 347.334 21.6941C347.564 21.3958 347.849 21.147 348.172 20.9618C348.496 20.7767 348.852 20.6589 349.219 20.6152C349.587 20.5714 349.96 20.6026 350.316 20.7068C350.672 20.8111 351.004 20.9864 351.294 21.2228C351.584 21.4593 351.826 21.7521 352.006 22.0847C352.186 22.4172 352.301 22.783 352.343 23.1611C352.386 23.5392 352.356 23.9222 352.254 24.2883L345.407 50.3801C345.306 50.7459 345.135 51.0876 344.905 51.3856C344.675 51.6836 344.39 51.9321 344.066 52.1169C343.743 52.3016 343.387 52.4191 343.02 52.4625C342.652 52.5059 342.28 52.4744 341.924 52.3698Z"
                                    fill="#F5990A" stroke="#F5990A" stroke-width="10" stroke-miterlimit="10" />
                                <path
                                    d="M239.458 45.0286H211.69L192.074 65.1986V93.7504L211.69 113.92H239.458L259.074 93.7504V65.1986L239.458 45.0286ZM251.63 90.5737L236.369 106.266H214.78L199.519 90.5737V68.3752L214.78 52.6832H236.369L251.63 68.3752V90.5737Z"
                                    fill="white" />
                                <path
                                    d="M225.573 98.6129C227.629 98.6129 229.295 96.8993 229.295 94.7856C229.295 92.6718 227.629 90.9583 225.573 90.9583C223.517 90.9583 221.851 92.6718 221.851 94.7856C221.851 96.8993 223.517 98.6129 225.573 98.6129Z"
                                    fill="white" />
                                <path d="M221.851 60.3367H229.295V87.1279H221.851V60.3367Z" fill="white" />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="tw-self-end tw-flex tw-flex-col tw-w-full ">
            <q-separator class="tw-mx-2 tw-text-home-deli-gray" />
            <div class="tw-flex tw-flex-row tw-justify-between tw-h-16 tw-w-full tw-text-lg tw-font-bold tw-items-center tw-px-4"
                v-if="!loadingShopCart">
                <span>{{ t('cartItems.subtotal') }}</span>
                <span class="tw-text-primary">{{ shopCart.subtotal_formatted }}</span>
            </div>
            <div class="tw-flex tw-flex-row tw-justify-between tw-h-16 tw-w-full tw-text-lg tw-font-bold tw-items-center tw-px-4"
                v-else>
                <q-skeleton width="100px" height="30px" :animation="'pulse'" />
                <q-skeleton width="100px" height="30px" :animation="'pulse'" />
            </div>
            <div
                class="tw-bg-home-deli-gray tw-w-full tw-p-2 tw-h-14 tw-flex tw-flex-row tw-justify-end tw-items-center tw-gap-4 ">
                <PrimaryButton :loading="loadingShopCart" @click="clearCart">{{ t('fields.clear') }}</PrimaryButton>
                <SecondaryButton :loading="loadingShopCart || loadingFinishCart" @click="finishCart">{{ t('actions.finish') }}
                </SecondaryButton>
            </div>
        </div>
    </div>
</template>